"use client";

import { useState, useEffect } from "react";
import { useParams } from "next/navigation";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import logger from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Image from "next/image";
import { ChevronDown, ChevronUp, RefreshCw, Download, X, AlertCircle } from "lucide-react";
import { createRechargeOrder, getRechargeRecords } from "@/lib/recharge";

interface RechargeRecord {
  id: string;
  amount: number;
  paymentAmount: number;
  paymentMethod: "支付宝" | "微信" | "银行卡";
  orderNo: string;
  status: "pending" | "completed" | "failed";
  createdTime: string;
}

export default function RechargePage() {
  const params = useParams();
  const locale = (params?.locale as string) || "zh";
  const isChinese = locale === "zh";

  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [redeemCode, setRedeemCode] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState<string | null>(null);
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  const [rechargeRecords, setRechargeRecords] = useState<RechargeRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(true);

  const rechargeAmounts = isChinese ? [100, 200, 500, 1000, 5000] : [5, 10, 50, 100, 500];
  const currencySymbol = isChinese ? "¥" : "$";
  const minAmount = isChinese ? 35 : 5;

  const paymentOptions = isChinese
    ? [
        { label: "支付宝", value: "alipay", icon: "/img/alipay.png", iconDark: "" },
        { label: "兑换码", value: "redeem", icon: "/img/redeem.png", iconDark: "" }
      ]
    : [
        { label: "Cryptomus (USDT)", value: "cryptomus", icon: "/img/logo/cryptomus_black.png", iconDark: "/img/logo/cryptomus_white.png" },
        { label: "Redeem Code", value: "redeem", icon: "/img/redeem.png", iconDark: "" }
      ];

  // 加载充值记录（暂时禁用对后端的静态/模拟数据接入，保持为空，后端接口准备好后再恢复）
  useEffect(() => {
    loadRechargeRecords();
  }, []);

  const loadRechargeRecords = async () => {
    try {
      setLoadingRecords(true);
      // 从后端获取充值记录；如果后端没有专门接口，getRechargeRecords 会从日志中筛选 type=1 的充值记录
      const records = await getRechargeRecords();
      setRechargeRecords(records || []);
    } catch (err: unknown) {
      // 静默处理错误，不影响支付功能
      logger.info(
        "加载充值记录失败（不影响支付）:",
        err instanceof Error ? err.message : String(err)
      );
      // 设置空数组，不显示错误
      setRechargeRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const getFinalAmount = () => {
    const amount = selectedAmount || (customAmount ? parseInt(customAmount) : 0);
    const fee = 0;
    return {
      amount,
      fee: Math.round(fee * 100) / 100,
      total: Math.round((amount + fee) * 100) / 100,
    };
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentLink(null);
    setPaymentForm(null);
    setPaymentOrderId(null);
  };

  const openPaymentForm = (html: string) => {
    const payWin = window.open("", "_blank");
    if (!payWin) {
      toast.error(isChinese ? "请允许浏览器弹窗" : "Please allow pop-ups");
      return;
    }
    payWin.document.write(html);
    payWin.document.close();

    // 延迟执行，确保文档完全加载并提交表单
    setTimeout(() => {
      const forms = payWin.document.forms;
      if (forms && forms.length > 0) {
        forms[0].submit();
        // logger.debug("✅ 表单已自动提交");
      } else {
      }
    }, 100);
  };

  const handleConfirmPayment = async () => {
    if (selectedPaymentMethod === "redeem") {
      // 兑换码支付流程
      if (!redeemCode || redeemCode.trim() === "") {
        toast.error(isChinese ? "请输入兑换码" : "Please enter redeem code");
        return;
      }

      try {
        setCreatingOrder(true);
        const order = await createRechargeOrder({
          amount: 0,
          pay_type: "redeem",
          code: redeemCode.trim()
        });

        toast.success(isChinese ? "兑换成功！" : "Redeemed successfully!");
        setRedeemCode("");
        setSelectedPaymentMethod(null);

        // 刷新充值记录
        setTimeout(() => {
          loadRechargeRecords();
        }, 1000);
      } catch (err: unknown) {
        if (err instanceof Error) {
          logger.error("兑换码核销失败:", err.message);
          toast.error(err.message || (isChinese ? "兑换失败" : "Redeem failed"));
        } else {
          logger.error("兑换码核销失败:", String(err));
          toast.error(isChinese ? "兑换失败" : "Redeem failed");
        }
      } finally {
        setCreatingOrder(false);
      }
      return;
    }

    // 原有支付流程
    if (!selectedAmount && !customAmount) {
      toast.error(isChinese ? "请选择或输入充值金额" : "Please select or enter an amount");
      return;
    }

    if (!selectedPaymentMethod) {
      toast.error(isChinese ? "请选择支付方式" : "Please select a payment method");
      return;
    }

    try {
      setCreatingOrder(true);
      const amount = selectedAmount || (customAmount ? parseInt(customAmount) : 0);

      // 根据选择的支付方式决定 pay_type
      const pay_type = selectedPaymentMethod === "cryptomus" ? "cryptomus" : "alipay";
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      const order = await createRechargeOrder({ amount, pay_type, device: isMobile ? "mobile" : "pc" });

      const payUrl = order.pay_url || "";
      const payForm = order.form || null;

      // 支付宝移动端：直接跳转拉起支付宝 App
      if (pay_type === "alipay" && isMobile) {
        if (payUrl) {
          window.location.href = payUrl;
          return;
        }
        if (payForm) {
          document.open();
          document.write(payForm);
          document.close();
          return;
        }
      }

      setPaymentLink(payForm ? null : payUrl || null);
      setPaymentForm(payForm);
      setPaymentOrderId(order.order_id);
      setShowPaymentModal(true);
      toast.success(isChinese ? "支付链接已生成" : "Payment link generated");

      // 自动打开表单或链接
      if (payForm) {
        openPaymentForm(payForm);
      } else if (payUrl) {
        window.open(payUrl, "_blank");
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        logger.error("创建充值订单失败:", err.message);
        toast.error(err.message || (isChinese ? "创建充值订单失败" : "Failed to create order"));
      } else {
        logger.error("创建充值订单失败:", String(err));
        toast.error(isChinese ? "创建充值订单失败" : "Failed to create order");
      }
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleCustomAmountBlur = () => {
    if (!customAmount) return;

    const amount = parseInt(customAmount);

    if (amount < 0) {
      setErrorMessage("充值金额不能为负数");
      setShowErrorModal(true);
      setCustomAmount("");
      return;
    }

    if (amount < minAmount) {
      setErrorMessage(
        isChinese
          ? `充值金额不能少于5美元(${minAmount}元)`
          : `Minimum recharge amount is $${minAmount}`
      );
      setShowErrorModal(true);
      setCustomAmount("");
      return;
    }
  };

  const handlePaymentComplete = async () => {
    try {
      setSavingPayment(true);
      // 支付完成后刷新记录
      toast.success(isChinese ? "支付完成！" : "Payment completed!");
      setShowPaymentModal(false);
      setSelectedAmount(null);
      setCustomAmount("");
      setSelectedPaymentMethod(null);
      setPaymentLink(null);
      setPaymentForm(null);
      setPaymentOrderId(null);

      // 延迟后刷新记录
      setTimeout(() => {
        loadRechargeRecords();
      }, 1000);
    } catch (err: unknown) {
      if (err instanceof Error) {
        logger.error("支付失败:", err);
        toast.error((isChinese ? "支付失败: " : "Payment failed: ") + err.message);
      } else {
        logger.error("支付失败:", String(err));
        toast.error(isChinese ? "支付失败" : "Payment failed");
      }
    } finally {
      setSavingPayment(false);
    }
  };

  const handleRefresh = () => {
    loadRechargeRecords();
    toast.success(isChinese ? "已刷新充值记录" : "Records refreshed");
  };

  const handleDownload = () => {
    toast.success("记录已下载");
  };

  const finalAmount = getFinalAmount();
  // USD conversion: for CNY, 1 USD = 7 CNY; for other locales amount is already USD
  const usdChargeValue = isChinese
    ? Math.round((finalAmount.amount / 7) * 1000) / 1000
    : finalAmount.amount;
  const usdChargeDisplay = Number.isInteger(usdChargeValue)
    ? `$${usdChargeValue}`
    : `$${usdChargeValue.toFixed(3)}`;

  const statusText: Record<string, string> = isChinese
    ? { pending: "处理中", completed: "已完成", failed: "已失败" }
    : { pending: "Pending", completed: "Completed", failed: "Failed" };
  const statusColor: Record<string, string> = {
    pending: "text-yellow-600",
    completed: "text-green-600",
    failed: "text-red-600",
  };

  return (
    <div className="w-full min-h-screen p-3 flex justify-center">
      <div className="w-full space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-[30px] mb-2 text-foreground font-bold ">{isChinese ? "账户充值" : "Account Recharge"}</h1>
          <p className="text-sm text-muted-foreground">{isChinese ? "为您的账户充值，享受更多高级服务和功能" : "Top up your account to enjoy more premium services"}</p>
        </div>

        <Card className="rounded-2xl shadow-sm py-3 bg-card">
          <CardContent className="">
            <h3 className="text-lg font-semibold mb-4 text-foreground">{isChinese ? "选择充值金额" : "Select Amount"}</h3>

            {selectedPaymentMethod !== "redeem" && (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-6">
                  {rechargeAmounts.map(amount => (
                    <Button
                      key={amount}
                      variant={selectedAmount === amount ? "default" : "outline"}
                      className={`p-3 font-semibold ${
                        selectedAmount === amount
                          ? ""
                          : "bg-secondary text-foreground hover:bg-secondary/80"
                      }`}
                      onClick={() => {
                        setSelectedAmount(amount);
                        setCustomAmount(amount.toString());
                      }}
                    >
                      {currencySymbol}{amount}
                    </Button>
                  ))}
                </div>

                <div className="flex justify-center">
                  <p className="mr-2 text-[32px] text-foreground">{currencySymbol}</p>
                  <Input
                    type="number"
                    value={customAmount}
                    onChange={e => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    onBlur={handleCustomAmountBlur}
                    placeholder={isChinese ? "输入自定义金额" : "Enter custom amount"}
                    className="w-4/5 text-center placeholder:text-center"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  {isChinese ? `最小充值金额5美元(${minAmount}元)` : `Minimum recharge amount: $${minAmount}`}
                </p>
              </>
            )}

            {selectedPaymentMethod === "redeem" && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground text-center">
                  {isChinese ? "使用兑换码充值无需输入金额，充值额度由兑换码决定" : "Redeem code amount is determined by the code itself"}
                </p>
                <Input
                  type="text"
                  value={redeemCode}
                  onChange={e => setRedeemCode(e.target.value)}
                  placeholder={isChinese ? "请输入兑换码" : "Enter redeem code"}
                  className="text-center placeholder:text-center"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm py-3 bg-card">
          <CardContent className="">
            <h3 className="text-lg font-semibold mb-4 text-foreground">{isChinese ? "选择支付方式" : "Payment Method"}</h3>

            <div className="flex flex-col gap-4">
              {paymentOptions.map(option => (
                <Button
                  key={option.value}
                  variant="outline"
                  onClick={() =>
                    setSelectedPaymentMethod(option.value)
                  }
                  className={`p-4 h-auto font-semibold w-full flex items-center gap-3 justify-start ${
                    selectedPaymentMethod === option.value
                      ? "border-primary bg-primary/10"
                      : "bg-secondary hover:bg-secondary/80"
                  }`}
                >
                  <div
                    className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                      selectedPaymentMethod === option.value
                        ? "border-primary bg-primary"
                        : "border-muted-foreground/30"
                    }`}
                  >
                    {selectedPaymentMethod === option.value && (
                      <span className="text-white text-xs font-bold">✓</span>
                    )}
                  </div>
                  {option.iconDark ? (
                    <>
                      <Image src={option.icon} alt={option.label} width={28} height={28} className="rounded dark:hidden" />
                      <Image src={option.iconDark} alt={option.label} width={28} height={28} className="rounded hidden dark:block" />
                    </>
                  ) : (
                    <Image src={option.icon} alt={option.label} width={28} height={28} className="rounded" />
                  )}
                  <span
                    className={
                      selectedPaymentMethod === option.value
                        ? "text-primary"
                        : "text-foreground"
                    }
                  >
                    {option.label}
                  </span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl shadow-sm py-3 bg-card">
          <CardContent className="">
            <h3 className="text-lg font-semibold mb-4 text-foreground">{isChinese ? "订单信息" : "Order Summary"}</h3>

            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm text-muted-foreground">{isChinese ? "充值金额" : "Amount"}</span>
                <span className="text-lg font-semibold text-foreground">
                  {selectedPaymentMethod === "redeem"
                    ? (isChinese ? "由兑换码决定" : "By code")
                    : `${currencySymbol}${finalAmount.amount}`}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm text-muted-foreground">{isChinese ? "支付方式" : "Payment"}</span>
                <span className="text-lg font-semibold text-foreground">
                  {selectedPaymentMethod
                    ? paymentOptions.find(o => o.value === selectedPaymentMethod)?.label || selectedPaymentMethod
                    : (isChinese ? "未选择" : "Not selected")}
                </span>
              </div>
              <div className="flex justify-between items-center py-2  border-b-2 border-primary/30">
                <span className="text-sm font-semibold text-primary">
                  {isChinese ? "应付金额" : "Total"}
                </span>
                <span className="text-2xl font-bold text-primary">
                  {selectedPaymentMethod === "redeem"
                    ? (isChinese ? "无需支付" : "Free")
                    : `${currencySymbol}${finalAmount.total}`}
                </span>
              </div>

              {isChinese && (
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">汇率</span>
                  <span className="text-sm text-muted-foreground">1 USD = 7 CNY</span>
                </div>
              )}

              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm font-semibold text-primary">
                  {isChinese ? "充值额度 (USD)" : "Credit (USD)"}
                </span>
                <span className="text-2xl font-bold text-primary">
                  {loadingRecords ? (isChinese ? "加载中..." : "Loading...") : selectedPaymentMethod === "redeem" ? (isChinese ? "由兑换码决定" : "By code") : usdChargeDisplay}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="flex sm:justify-end sm:mr-6">
          <Button
            onClick={handleConfirmPayment}
            className="w-full sm:w-auto px-8"
            disabled={creatingOrder}
          >
            {creatingOrder
              ? (isChinese ? "处理中..." : "Processing...")
              : selectedPaymentMethod === "redeem"
                ? (isChinese ? "确认兑换" : "Confirm Redeem")
                : (isChinese ? "确认支付" : "Confirm Payment")}
          </Button>
        </div>
        {/* 充值记录卡片 */}
        <Card className="rounded-2xl shadow-sm bg-card" id="rechargeRecords">
          <CardContent className="p-6 py-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">{isChinese ? "充值记录" : "Recharge History"}</h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  className="flex items-center gap-2"
                >
                  <RefreshCw size={16} />
                  {isChinese ? "刷新" : "Refresh"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  className="flex items-center gap-2"
                >
                  <Download size={16} />
                  {isChinese ? "下载" : "Download"}
                </Button>
              </div>
            </div>

            {/* 表格 */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr className="text-muted-foreground">
                    <th className="text-left py-3 px-4 font-semibold">{isChinese ? "充值额度" : "Credit"}</th>
                    <th className="text-left py-3 px-4 font-semibold">{isChinese ? "支付金额" : "Paid"}</th>
                    <th className="text-left py-3 px-4 font-semibold">{isChinese ? "支付方式" : "Method"}</th>
                    <th className="text-left py-3 px-4 font-semibold">{isChinese ? "订单号" : "Order ID"}</th>
                    <th className="text-left py-3 px-4 font-semibold">{isChinese ? "状态" : "Status"}</th>
                    <th className="text-left py-3 px-4 font-semibold">{isChinese ? "创建时间" : "Created"}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRecords ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-6 px-4 text-center text-muted-foreground"
                      >
                        加载中...
                      </td>
                    </tr>
                  ) : rechargeRecords.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-6 px-4 text-center text-muted-foreground"
                      >
                        {isChinese ? "暂无数据" : "No records"}
                      </td>
                    </tr>
                  ) : (
                    rechargeRecords.map(record => (
                      <tr
                        key={record.id}
                        className="border-b border-border hover:bg-secondary"
                      >
                        <td className="py-4 px-4 text-foreground">
                          ${record.amount}
                        </td>
                        <td className="py-4 px-4 text-foreground">
                          ${record.paymentAmount}
                        </td>
                        <td className="py-4 px-4 text-foreground">
                          {record.paymentMethod}
                        </td>
                        <td className="py-4 px-4 font-mono text-xs text-foreground">
                          {record.orderNo}
                        </td>
                        <td className={`py-4 px-4 font-semibold ${statusColor[record.status]}`}>
                          {statusText[record.status]}
                        </td>
                        <td className="py-4 px-4 text-muted-foreground">
                          {record.createdTime}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showPaymentModal} onOpenChange={(open) => { if (!open) handleClosePaymentModal(); }}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedPaymentMethod ? paymentOptions.find(o => o.value === selectedPaymentMethod)?.label : ""}</DialogTitle>
          </DialogHeader>

              <div className="flex flex-col items-center gap-4">
                {/* 直接使用支付链接 */}
                {paymentLink ? (
                  <>
                    <Button
                      className="w-full"
                      onClick={() => {
                        window.open(paymentLink as string, "_blank", "noopener,noreferrer");
                      }}
                    >
                      {isChinese ? "打开支付宝支付页面" : "Open Payment Page"}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center break-words">
                      {isChinese ? "如果未自动跳转，可复制链接在浏览器中打开：" : "If not redirected, copy the link below:"}
                    </p>
                    <div className="w-full border border-border rounded-md p-3 text-xs break-words bg-secondary">
                      {paymentLink}
                    </div>
                  </>
                ) : paymentForm ? (
                  <div className="w-full flex flex-col gap-3">
                    <Button
                      className="w-full"
                      onClick={() => {
                        openPaymentForm(paymentForm);
                      }}
                    >
                      {isChinese ? "重新打开支付宝支付页面" : "Reopen Payment Page"}
                    </Button>
                    <div className="text-xs text-muted-foreground text-center">
                      {isChinese ? "已生成支付表单，将在新窗口自动提交。如果未弹出，请点击上方按钮。" : "Payment form generated. If the window didn't open, click the button above."}
                    </div>
                  </div>
                ) : (
                  <div className="w-48 h-48 bg-secondary rounded-lg mb-6 flex items-center justify-center border-2 border-border">
                    <span className="text-muted-foreground text-sm">{isChinese ? "等待生成支付信息..." : "Generating payment info..."}</span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <p className="text-xs text-muted-foreground mb-1">{isChinese ? "本次支付金额" : "Payment Amount"}</p>
                  <p className="text-2xl font-bold text-primary">
                    {currencySymbol}{finalAmount.total}
                  </p>
                  {paymentOrderId && (
                    <p className="text-xs text-muted-foreground mt-2">{isChinese ? "订单号：" : "Order: "}{paymentOrderId}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleClosePaymentModal}>
                  {isChinese ? "取消支付" : "Cancel"}
                </Button>
                <Button
                  className="flex-1"
                  onClick={handlePaymentComplete}
                  disabled={savingPayment}
                >
                  {savingPayment ? (isChinese ? "处理中..." : "Processing...") : (isChinese ? "支付完成" : "Payment Done")}
                </Button>
              </div>
        </DialogContent>
      </Dialog>

      {/* 错误提示弹窗 */}
      <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center text-center gap-6 pt-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10">
              <AlertCircle className="text-destructive" size={32} />
            </div>
            <p className="text-base text-foreground leading-relaxed">{errorMessage}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => setShowErrorModal(false)}
            >
              {isChinese ? "确定" : "OK"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
